import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axiosInstance from '../../config/axiosConfig';
import Swal from 'sweetalert2';

const QEC = () => {
  const [qecInfo, setQecInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Contact form states
  const [contactForm, setContactForm] = useState({
    directorName: '',
    directorTitle: '',
    phoneNumber: '',
    email: '',
    officeLocation: ''
  });
  const [editingContact, setEditingContact] = useState(false);
  
  // General info form states
  const [generalForm, setGeneralForm] = useState({
    description: '',
    mission: '',
    vision: ''
  });
  const [editingGeneral, setEditingGeneral] = useState(false);
  
  // Document form states
  const [documentDialog, setDocumentDialog] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    title: '',
    description: '',
    document: null
  });

  useEffect(() => {
    fetchQECInfo();
    fetchDocuments();
  }, []);

  const fetchQECInfo = async () => {
    try {
      const response = await axiosInstance.get('/qec/info');
      const data = response.data.data;
      setQecInfo(data);
      setContactForm({
        directorName: data.directorName || '',
        directorTitle: data.directorTitle || '',
        phoneNumber: data.phoneNumber || '',
        email: data.email || '',
        officeLocation: data.officeLocation || ''
      });
      setGeneralForm({
        description: data.description || '',
        mission: data.mission || '',
        vision: data.vision || ''
      });
    } catch (error) {
      setError('Failed to fetch QEC information');
      console.error('Error fetching QEC info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axiosInstance.get('/qec/documents');
      setDocuments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleContactUpdate = async () => {
    try {
      await axiosInstance.put('/qec/contact', contactForm);
      setEditingContact(false);
      await fetchQECInfo();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Contact information updated successfully',
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to update contact information',
        timer: 1500,
      });
    }
  };

  const handleGeneralUpdate = async () => {
    try {
      await axiosInstance.put('/qec/info', generalForm);
      setEditingGeneral(false);
      await fetchQECInfo();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'General information updated successfully',
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to update general information',
        timer: 1500,
      });
    }
  };

  const handleDocumentUpload = async () => {
    try {
      const formData = new FormData();
      formData.append('title', documentForm.title);
      formData.append('description', documentForm.description);
      if (documentForm.document) {
        formData.append('document', documentForm.document);
      }

      await axiosInstance.post('/qec/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setDocumentDialog(false);
      setDocumentForm({ title: '', description: '', document: null });
      await fetchDocuments();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: 'Document uploaded successfully',
        timer: 1500,
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to upload document',
        timer: 1500,
      });
    }
  };

  const handleDocumentDelete = async (documentId) => {
    try {
      await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then(async (result) => {
        if (result.isConfirmed) {
          await axiosInstance.delete(`/qec/documents/${documentId}`);
          await fetchDocuments();
          Swal.fire(
            'Deleted!',
            'Document has been deleted.',
            'success'
          );
        }
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to delete document',
        timer: 1500,
      });
    }
  };

  const handleFileChange = (event) => {
    setDocumentForm({
      ...documentForm,
      document: event.target.files[0]
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, color: '#1976d2' }}>
        Quality Enhancement Cell (QEC)
      </Typography>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="primary">
                  Contact Information
                </Typography>
                <IconButton
                  onClick={() => setEditingContact(!editingContact)}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
              </Box>

              {editingContact ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Director Name"
                    value={contactForm.directorName}
                    onChange={(e) => setContactForm({...contactForm, directorName: e.target.value})}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Director Title"
                    value={contactForm.directorTitle}
                    onChange={(e) => setContactForm({...contactForm, directorTitle: e.target.value})}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={contactForm.phoneNumber}
                    onChange={(e) => setContactForm({...contactForm, phoneNumber: e.target.value})}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Office Location"
                    value={contactForm.officeLocation}
                    onChange={(e) => setContactForm({...contactForm, officeLocation: e.target.value})}
                    margin="normal"
                  />
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleContactUpdate}
                      sx={{ mr: 1 }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={() => setEditingContact(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    <strong>Director:</strong> {qecInfo?.directorName}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Title:</strong> {qecInfo?.directorTitle}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Phone:</strong> {qecInfo?.phoneNumber}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Email:</strong> {qecInfo?.email}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Location:</strong> {qecInfo?.officeLocation}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* General Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="primary">
                  General Information
                </Typography>
                <IconButton
                  onClick={() => setEditingGeneral(!editingGeneral)}
                  color="primary"
                >
                  <EditIcon />
                </IconButton>
              </Box>

              {editingGeneral ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Description"
                    value={generalForm.description}
                    onChange={(e) => setGeneralForm({...generalForm, description: e.target.value})}
                    margin="normal"
                    multiline
                    rows={3}
                  />
                  <TextField
                    fullWidth
                    label="Mission"
                    value={generalForm.mission}
                    onChange={(e) => setGeneralForm({...generalForm, mission: e.target.value})}
                    margin="normal"
                    multiline
                    rows={3}
                  />
                  <TextField
                    fullWidth
                    label="Vision"
                    value={generalForm.vision}
                    onChange={(e) => setGeneralForm({...generalForm, vision: e.target.value})}
                    margin="normal"
                    multiline
                    rows={3}
                  />
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleGeneralUpdate}
                      sx={{ mr: 1 }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={() => setEditingGeneral(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    <strong>Description:</strong>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {qecInfo?.description}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Mission:</strong>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {qecInfo?.mission}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Vision:</strong>
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {qecInfo?.vision}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Documents Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="primary">
                  Documents
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setDocumentDialog(true)}
                >
                  Add Document
                </Button>
              </Box>

              {documents.length === 0 ? (
                <Typography variant="body2" color="textSecondary" align="center" py={4}>
                  No documents available
                </Typography>
              ) : (
                <List>
                  {documents.map((doc) => (
                    <React.Fragment key={doc._id}>
                      <ListItem>
                        <ListItemText
                          primary={doc.title}
                          secondary={
                            <Box>
                              <Typography variant="body2" color="textSecondary">
                                {doc.description}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Uploaded: {new Date(doc.uploadDate).toLocaleDateString()}
                              </Typography>
                              <Box mt={1}>
                                <Chip
                                  label={doc.isActive ? 'Active' : 'Inactive'}
                                  color={doc.isActive ? 'success' : 'default'}
                                  size="small"
                                />
                              </Box>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => window.open(doc.fileUrl, '_blank')}
                            sx={{ mr: 1 }}
                          >
                            <DownloadIcon />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={() => handleDocumentDelete(doc._id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Document Upload Dialog */}
      <Dialog open={documentDialog} onClose={() => setDocumentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New Document</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Document Title"
            value={documentForm.title}
            onChange={(e) => setDocumentForm({...documentForm, title: e.target.value})}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={documentForm.description}
            onChange={(e) => setDocumentForm({...documentForm, description: e.target.value})}
            margin="normal"
            multiline
            rows={3}
          />
          <input
            accept=".pdf,.doc,.docx,.txt"
            style={{ display: 'none' }}
            id="document-file"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="document-file">
            <Button variant="outlined" component="span" sx={{ mt: 2 }}>
              Choose File
            </Button>
          </label>
          {documentForm.document && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected: {documentForm.document.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocumentDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDocumentUpload}
            variant="contained"
            disabled={!documentForm.title || !documentForm.document}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QEC; 